import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Table, 
  Button, 
  Form, 
  InputGroup,
  Modal,
  Badge,
  ButtonGroup
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import * as Icons from 'react-icons/fa';
import { productsAPI } from '../services/api';
import Icon from '../components/Icon';

interface Product {
  _id: string;
  name: string;
  nameAr: string;
  description: string;
  descriptionAr: string;
  category: string;
  images: {
    path: string;
    secure_url: string;
    public_id: string;
    isMain: boolean;
    _id: string;
  }[];
  catalogFile?: string;
  catalog?: {
    file: string;
    fileName?: string;
    fileType?: string;
    description?: string;
  };
  hasCatalog?: boolean;
  createdAt: string;
}

const Products: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  // Function to fetch products from API
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await productsAPI.getProducts();
      setProducts(response.data);
    } catch (err: any) {
      setError('Failed to fetch products');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Filter products based on search term
  const filteredProducts = products.filter((product) =>
    (product.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (product.nameAr?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (product.description?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (product.descriptionAr?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Confirm product deletion
  const confirmDelete = (product: Product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  // Handle product deletion
  const handleDelete = async () => {
    if (!productToDelete) return;
    
    try {
      await productsAPI.deleteProduct(productToDelete._id);
      setProducts(products.filter(p => p._id !== productToDelete._id));
      setShowDeleteModal(false);
      setProductToDelete(null);
    } catch (err: any) {
      setError('Failed to delete product');
      console.error(err);
    }
  };

  if (loading && products.length === 0) {
    return (
      <Container>
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading products...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mb-4 align-items-center">
        <Col>
          <h1 className="mb-0">Products</h1>
        </Col>
        <Col xs="auto">
          <Link to="/products/new">
            <Button variant="primary">
              <Icon icon={Icons.FaPlus} className="me-2" /> Add Product
            </Button>
          </Link>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white py-3">
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-0">All Products</h5>
            </Col>
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <Icon icon={Icons.FaSearch} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </InputGroup>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="p-0">
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Category</th>
                <th>Description</th>
                <th>Catalog</th>
                <th>Date Added</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr key={product._id}>
                  <td>
                    {product.images && product.images.length > 0 ? (
                      <img 
                        src={`${product.images[0].secure_url}`} 
                        alt={product.name} 
                        style={{ width: '50px', height: '50px', objectFit: 'cover' }} 
                        onError={(e) => {
                          // Replace with inline SVG instead of external placeholder
                          const target = e.currentTarget;
                          target.onerror = null; // Prevent infinite loop
                          
                          // Create an SVG element with product initial
                          const initial = (product.name || 'P')[0].toUpperCase();
                          target.style.display = 'none';
                          
                          // Create a colored div with text instead
                          const parent = target.parentElement;
                          if (parent) {
                            const div = document.createElement('div');
                            div.style.width = '50px';
                            div.style.height = '50px';
                            div.style.backgroundColor = '#f0f0f0';
                            div.style.display = 'flex';
                            div.style.alignItems = 'center';
                            div.style.justifyContent = 'center';
                            div.style.fontWeight = 'bold';
                            div.style.color = '#555';
                            div.textContent = initial;
                            parent.appendChild(div);
                          }
                        }}
                      />
                    ) : (
                      <div 
                        className="bg-light d-flex align-items-center justify-content-center" 
                        style={{ width: '50px', height: '50px' }}
                      >
                        <Icon icon={Icons.FaImage} />
                      </div>
                    )}
                  </td>
                  <td>{product.name || 'Unnamed Product'} <br /> {product.nameAr || ''}</td>
                  <td>{product.category || 'No category'}</td>
                  <td>
                      {product.description?.length > 50
                        ? `${product.description.substring(0, 50)}...`
                        : product.description || 'No description'}
                        <br />
                        {product.descriptionAr?.length > 50
                        ? `${product.descriptionAr.substring(0, 50)}...`
                        : product.descriptionAr || ''}
                  </td>
                  <td>
                    {(product.catalogFile || (product.catalog && product.catalog.file) || product.hasCatalog) ? (
                      <a 
                        href={`${process.env.REACT_APP_API_URL_WITHOUT_API}/${product.catalogFile || (product.catalog && product.catalog.file)}`} 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary"
                      >
                        <Icon icon={Icons.FaFilePdf} className="me-1" /> View
                      </a>
                    ) : (
                      <span className="text-muted">No catalog</span>
                    )}
                  </td>
                  <td>{formatDate(product.createdAt)}</td>
                  <td>
                    <ButtonGroup size="sm">
                      {/* <Button 
                        variant="info" 
                        as={Link as any} 
                        to={`/products/${product._id}`}
                        title="View Product"
                      >
                        <Icon icon={Icons.FaEye} />
                      </Button> */}
                      <Button 
                        variant="primary" 
                        as={Link as any} 
                        to={`/products/edit/${product._id}`}
                        title="Edit Product"
                      >
                        <Icon icon={Icons.FaPencilAlt} />
                      </Button>
                      <Button 
                        variant="danger"
                        title="Delete Product"
                        onClick={() => confirmDelete(product)}
                      >
                        <Icon icon={Icons.FaTrash} />
                      </Button>
                    </ButtonGroup>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the product "{productToDelete?.name}"? 
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Products; 